import { Injectable } from '@angular/core';

import { readSheet, parseData } from 'read-excel-file/browser';

import { z } from 'zod';

import * as Papa from 'papaparse';
import { ParseResult } from 'papaparse';

import * as Sentry from '@sentry/angular';
import { Schema } from 'read-excel-file/types/parseData/parseDataSchema';
import { Observable, from, map, pipe } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SpreadsheetService {
  constructor() { }

  private readonly CSV_TYPES = [
    'text/csv',
  ];

  private readonly EXCEL_TYPES = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
  ];

  parseExcel<T>(file: File, schema: Schema): Observable<{ headers: string[] ; data: T[] ; errors: string[] }> {
    return from(readSheet(file)).pipe(
      map((rows) => {
        const data: T[] = [];
        const errors: string[] = [];
        let rowIdx = 0;
        for (const { object, errors: parseErrors } of parseData(rows, schema)) {
          rowIdx++;
          if (parseErrors) {
            parseErrors.forEach(error => { errors.push(`There was an error parsing column '${error.column}' on Row ${rowIdx}`) ; console.error(error); });
          }
          else
          {
            data.push(object as T);
          }
        }

        return {
          headers: Object.keys(schema),
          data,
          errors,
        }
      }),
    );
  }

  parseCsv<T extends z.ZodTypeAny>(
    file:File,
    schema: T,
    headerMap: Record<string, string>
  ): Observable<{ headers: string[] ; data: z.infer<T>[] ; errors: string[] }> {
    return new Observable((subscriber) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: false,
        complete: (results: ParseResult<Record<string, string | number | boolean>>) => {
          const data: z.infer<T>[] = [];
          const errors: string[] = [];

          // Handle any parse errors
          results.errors.forEach((parseError) => {
            errors.push(`${parseError.message} on row ${parseError.row}`);
          })

          // Now, validate our csv sheet data against our expected format, reporting errors
          results.data.forEach((row, index) => {
            const mappedRow: any = {};

            // Translate the user-friendly csv headers to our object keys
            Object.entries(headerMap).forEach(([columnName, propName]) => {
              mappedRow[propName] = row[columnName];
            });

            // Validate against our zod-schema for this object type
            const validationResult = schema.safeParse(mappedRow);
            if (validationResult.success) {
              data.push(validationResult.data);
            } else {
              const flatError = z.flattenError(validationResult.error);
              for (const col in flatError.fieldErrors) {
                const readableCol = Object.keys(headerMap).find(k => headerMap[k] === col);
                errors.push(`Column '${readableCol ?? col}' on Row ${index + 1} has errors: ${flatError.fieldErrors?.[col]?.join(', ')}`);
              }
            }
          });

          subscriber.next({
            headers: results.meta.fields ?? [],
            data,
            errors,
          });
          subscriber.complete();
        },
        error: (err) => {
          Sentry.captureException(err, {
            tags: {
              section: 'enroll',
              action: 'batch-enroll-file-parse',
              parseType: 'csv (PapaParse)',
            }
          }) ;

          subscriber.error(err);
        }
      });
    });
  }

  isExcelFile(file: File) : boolean {
    return this.EXCEL_TYPES.includes(file.type);
  }

  isCsvFile(file: File) : boolean {
    return this.CSV_TYPES.includes(file.type);
  }
}
